import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

// ─── Projects ─────────────────────────────────────────────────────

export async function getTaskboardProjects(token: string, forBacklog?: boolean) {
  let url = `${API_BASE}/taskboard/projects`;
  if (forBacklog) url += '?for_backlog=true';
  const res = await axios.get(url, authHeaders(token));
  return res.data;
}

// ─── Requirement Detail ──────────────────────────────────────────

export async function getRequirementDetail(id: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/requirement/${id}`, authHeaders(token));
  return res.data;
}

export async function getRequirementChildren(id: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/requirement/${id}/children`, authHeaders(token));
  return res.data;
}

export async function getRequirementActivities(id: string, token: string, limit?: number) {
  const params = limit ? `?limit=${limit}` : '';
  const res = await axios.get(`${API_BASE}/taskboard/requirement/${id}/activities${params}`, authHeaders(token));
  return res.data;
}

export async function getRequirementSprintHistory(id: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/requirement/${id}/sprint-history`, authHeaders(token));
  return res.data;
}

// ─── Workflow Columns ─────────────────────────────────────────────

export async function getColumns(token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/columns`, authHeaders(token));
  return res.data;
}

export async function createColumn(name: string, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/columns`, { name }, authHeaders(token));
  return res.data;
}

export async function updateColumn(id: string, name: string, token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/columns/${id}`, { name }, authHeaders(token));
  return res.data;
}

export async function deleteColumn(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/columns/${id}`, authHeaders(token));
  return res.data;
}

export async function reorderColumns(orderedIds: string[], token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/columns/reorder`, { ordered_ids: orderedIds }, authHeaders(token));
  return res.data;
}

// ─── Sprints ──────────────────────────────────────────────────────

export interface CreateSprintData {
  project_id?: string; // Optional - sprints are company-wide
  team_id?: string; // Optional - NULL means "All Teams"
  name: string;
  goal?: string;
  start_date: string;
  end_date: string;
}

export async function createSprint(data: CreateSprintData, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/sprints`, data, authHeaders(token));
  return res.data;
}

export async function getSprints(projectId: string, token: string) {
  let url = `${API_BASE}/taskboard/sprints`;
  if (projectId) url += `?project_id=${projectId}`;
  const res = await axios.get(url, authHeaders(token));
  return res.data;
}

export async function getSprintById(id: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/sprints/${id}`, authHeaders(token));
  return res.data;
}

export async function updateSprint(id: string, data: Partial<CreateSprintData>, token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/sprints/${id}`, data, authHeaders(token));
  return res.data;
}

export async function startSprint(id: string, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/sprints/${id}/start`, {}, authHeaders(token));
  return res.data;
}

export interface SprintCompletionAction {
  requirement_id: string;
  action: 'backlog' | 'next_sprint';
}

export interface CompletionData {
  actions: SprintCompletionAction[];
  existing_sprint_id?: string;
  create_next_sprint?: {
    name: string;
    goal?: string;
    start_date: string;
    end_date: string;
  };
}

export async function completeSprint(id: string, data: CompletionData, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/sprints/${id}/complete`, data, authHeaders(token));
  return res.data;
}

export async function deleteSprint(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/sprints/${id}`, authHeaders(token));
  return res.data;
}

// ─── Board ────────────────────────────────────────────────────────

export async function getBoardData(projectId: string, token: string, sprintId?: string, view?: 'stories' | 'subtasks') {
  let url = `${API_BASE}/taskboard/board`;
  const params: string[] = [];
  if (projectId) params.push(`project_id=${projectId}`);
  if (sprintId) params.push(`sprint_id=${sprintId}`);
  if (view && view !== 'stories') params.push(`view=${view}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const res = await axios.get(url, authHeaders(token));
  return res.data;
}

export async function moveCard(requirementId: string, columnId: string, order: number, token: string, forceOverride?: boolean) {
  const res = await axios.put(`${API_BASE}/taskboard/board/move`, {
    requirement_id: requirementId,
    column_id: columnId,
    order,
    force_override: forceOverride || false,
  }, authHeaders(token));
  return res.data;
}

// ─── Backlog ──────────────────────────────────────────────────────

export async function getBacklog(token: string, projectId?: string) {
  let url = `${API_BASE}/taskboard/backlog`;
  if (projectId) url += `?project_id=${projectId}`;
  const res = await axios.get(url, authHeaders(token));
  return res.data;
}

export async function assignRequirement(
  id: string,
  data: {
    description?: string;
    acceptance_criteria?: string;
    assigned_to?: string;
    story_type?: string;
    story_points?: number;
    labels?: string;
    due_date?: string;
    original_estimate_hours?: number;
    is_verified?: boolean;
    priority?: string;
  },
  token: string,
) {
  const res = await axios.put(`${API_BASE}/taskboard/backlog/${id}/assign`, data, authHeaders(token));
  return res.data;
}

export async function addToSprint(requirementIds: string[], sprintId: string, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/backlog/add-to-sprint`, {
    requirement_ids: requirementIds,
    sprint_id: sprintId,
  }, authHeaders(token));
  return res.data;
}

export async function removeFromSprint(requirementIds: string[], token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/backlog/remove-from-sprint`, {
    requirement_ids: requirementIds,
  }, authHeaders(token));
  return res.data;
}

export async function updateBacklogOrder(items: { id: string; order: number }[], token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/backlog/reorder`, { items }, authHeaders(token));
  return res.data;
}

export async function updateSprintOrder(sprintId: string, items: { id: string; order: number }[], token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/sprints/${sprintId}/reorder`, { items }, authHeaders(token));
  return res.data;
}

// ─── Checklist ───────────────────────────────────────────────────

export async function createChecklistItem(data: { requirement_id: string; title: string }, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/checklist`, data, authHeaders(token));
  return res.data;
}

export async function getChecklistItems(requirementId: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/checklist/${requirementId}`, authHeaders(token));
  return res.data;
}

export async function toggleChecklistItem(id: string, token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/checklist/${id}/toggle`, {}, authHeaders(token));
  return res.data;
}

export async function deleteChecklistItem(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/checklist/${id}`, authHeaders(token));
  return res.data;
}

// ─── Acceptance Criteria ──────────────────────────────────────────

export async function getAcceptanceCriteria(requirementId: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/acceptance-criteria/${requirementId}`, authHeaders(token));
  return res.data;
}

export async function createAcceptanceCriterion(data: { requirement_id: string; title: string }, token: string) {
  const res = await axios.post(`${API_BASE}/taskboard/acceptance-criteria`, data, authHeaders(token));
  return res.data;
}

export async function toggleAcceptanceCriterion(id: string, token: string) {
  const res = await axios.put(`${API_BASE}/taskboard/acceptance-criteria/${id}/toggle`, {}, authHeaders(token));
  return res.data;
}

export async function deleteAcceptanceCriterion(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/acceptance-criteria/${id}`, authHeaders(token));
  return res.data;
}

// ─── Attachments ─────────────────────────────────────────────────

export async function uploadAttachment(requirementId: string, file: File, token: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('requirement_id', requirementId);
  const res = await axios.post(`${API_BASE}/taskboard/attachments`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function getAttachments(requirementId: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/attachments/${requirementId}`, authHeaders(token));
  return res.data;
}

export async function deleteAttachment(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/attachments/${id}`, authHeaders(token));
  return res.data;
}

// ─── Time Logs ────────────────────────────────────────────────────

export async function logTime(
  data: { requirement_id: string; hours: number; description?: string; logged_date?: string },
  token: string,
) {
  const res = await axios.post(`${API_BASE}/taskboard/time-logs`, data, authHeaders(token));
  return res.data;
}

export async function getTimeLogs(requirementId: string, token: string) {
  const res = await axios.get(`${API_BASE}/taskboard/time-logs/${requirementId}`, authHeaders(token));
  return res.data;
}

export async function deleteTimeLog(id: string, token: string) {
  const res = await axios.delete(`${API_BASE}/taskboard/time-logs/${id}`, authHeaders(token));
  return res.data;
}
